import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useError } from "@/contexts/ErrorContext.jsx";
import { shouldReportApiError } from "@/api/api-utils.js";
import { useDataLoaderContext } from "@/contexts/DataLoaderContext.jsx";
import { useTagData } from "./useTagData.js";
import { useTrendColumns } from "./useTrendColumns.js";
import { useTrendData } from "./useTrendData.js";
import {
  getEnalysisAerialCoolers,
  getEnalysisAssetTrendsList,
  getEnalysisClearances,
  getEnalysisCylinders,
  getEnalysisDesignConditions,
  getEnalysisTrendsData,
} from "@/api/enalysis-api.js";
import { applyEnalysisTrendGaugeEnrichments } from "@/lib/enalysis-trends-gauge-utils.js";
import {
  filterEnalysisMeasurementsByTrendRange,
  getCommaSeparatedKeysForEnalysisTrendsData,
  mapAssetTrendsListItemsToParameters,
  mapEnalysisAssetTrendsListResponseToParameters,
  mergeEnalysisTrendsDataIntoParameters,
  parseAssetTrendsListResponse,
} from "@/lib/enalysis-trends-tag-utils.js";
import {
  ENALYSIS_TREND_LIST_SOURCE,
  ENALYSIS_TREND_ROW_CATEGORIES,
  ENALYSIS_TRENDS_DATA_RANGE_MONTHS,
} from "@/constants/enalysis-trends-constants.js";
import { arrayUtils } from "@/lib/utils.js";
import { formatNumber, removeCommasAndSpaces } from "@/lib/formatting-utils.js";
import { calculateAverageAndZScore, flattenTagDataToParameters } from "@/lib/tag-data-utils.js";
import { formatParameterValue } from "@/lib/parameter-status-utils.js";
import {
  createChartSeries,
  formatMultiSeriesTooltip,
} from "@/utils/chart-utils.js";

function enalysisErrorDetail(err) {
  if (err?.response?.status != null) {
    return `HTTP ${err.response.status}: ${err.message || ""}`;
  }
  return err?.message ?? (typeof err === "string" ? err : undefined);
}

/** Cache Enalysis trend rows per asset + Enalysis ids (same idea as TagDataContext for tag rows). */
/** @type {Map<string, { parameters: unknown[], triggerAtFetch: number }>} */
const enalysisTrendParametersCache = new Map();
/** @type {Map<string, Promise<unknown[]>>} */
const enalysisTrendParametersInflight = new Map();

function enalysisTrendCacheKey(assetId, compNum, compId) {
  return `${assetId}|${compNum}|${compId}|${ENALYSIS_TRENDS_DATA_RANGE_MONTHS}`;
}

/**
 * Trend list + optional /Trends measurements; mirrors prior effect behavior.
 * @param {{ onListParams?: (p: unknown[]) => void, onBeforeTrendValuesFetch?: () => void }} callbacks
 * @param {{ cylinders?: unknown, designConditions?: unknown, aerialCoolers?: object|null, clearances?: unknown }} [enrichmentContext]
 */
async function fetchEnalysisTrendParametersSequential(
  compressor,
  addError,
  callbacks,
  enrichmentContext
) {
  const { onListParams, onBeforeTrendValuesFetch } = callbacks ?? {};
  const data = await getEnalysisAssetTrendsList(
    compressor.enalysisCompressorNum,
    compressor.enalysisCompanyId
  );
  const parsed = parseAssetTrendsListResponse(data);
  let params =
    parsed.length > 0
      ? mapAssetTrendsListItemsToParameters(parsed)
      : mapEnalysisAssetTrendsListResponseToParameters(data);
  params = applyEnalysisTrendGaugeEnrichments(params, enrichmentContext);
  onListParams?.(params);
  if (parsed.length > 0) {
    const keysCsv = getCommaSeparatedKeysForEnalysisTrendsData(params);
    if (keysCsv) {
      onBeforeTrendValuesFetch?.();
      try {
        const trendsPayload = await getEnalysisTrendsData(
          compressor.enalysisCompressorNum,
          ENALYSIS_TRENDS_DATA_RANGE_MONTHS,
          compressor.enalysisCompanyId,
          keysCsv
        );
        params = mergeEnalysisTrendsDataIntoParameters(params, trendsPayload);
        return applyEnalysisTrendGaugeEnrichments(params, enrichmentContext);
      } catch (err) {
        if (shouldReportApiError(err)) {
          addError("Failed to load Enalysis trend data", enalysisErrorDetail(err));
        }
        return params;
      }
    }
  }
  return params;
}

/**
 * Custom hook for managing asset tag and trend data
 * Shared logic between Asset Vitals and Asset Trends pages
 * @param {Object} compressor - The compressor/asset object
 * @returns {Object} All state, handlers, and data needed for tag and trend management
 */
export function useAssetTagTrend(compressor) {
  const { addError } = useError();
  const { triggerCount } = useDataLoaderContext();
  const triggerCountRef = useRef(triggerCount);
  triggerCountRef.current = triggerCount;
  /** Guards Enalysis fetch callbacks so a stale run cannot update state after asset/effect changes. */
  const enalysisFetchGenerationRef = useRef(0);

  // Filter and sort state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedParameters, setSelectedParameters] = useState(new Set());
  const [sortColumn, setSortColumn] = useState("tagName");
  const [sortDirection, setSortDirection] = useState("asc");

  // Trend analysis state
  const [selectedTimeRange, setSelectedTimeRange] = useState("7 Days");
  const [previousTimeRange, setPreviousTimeRange] = useState("7 Days");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [isZoomed, setIsZoomed] = useState(false);

  // Cache for trend data: Map<paramId, Map<period, rawData>>
  // Periods: "24h", "7d", "30d"
  const [trendDataCache, setTrendDataCache] = useState(new Map());

  // Fetch tag data
  const { tagData, isLoading, error } = useTagData(
    compressor?.id,
    !!compressor
  );

  const [enalysisTrendParameters, setEnalysisTrendParameters] = useState([]);
  const [isLoadingEnalysisTrendValues, setIsLoadingEnalysisTrendValues] =
    useState(false);

  const enalysisTrendsGate =
    compressor?.optimizationEnabled === true
    && compressor?.enalysisCompressorNum != null
    && compressor?.enalysisCompanyId != null;

  useEffect(() => {
    if (!enalysisTrendsGate) {
      setEnalysisTrendParameters([]);
      setIsLoadingEnalysisTrendValues(false);
      return;
    }

    const key = enalysisTrendCacheKey(
      compressor.id,
      compressor.enalysisCompressorNum,
      compressor.enalysisCompanyId
    );

    const cached = enalysisTrendParametersCache.get(key);
    if (cached && cached.triggerAtFetch === triggerCount) {
      setEnalysisTrendParameters(cached.parameters);
      setIsLoadingEnalysisTrendValues(false);
      return;
    }

    const fetchGen = triggerCount;
    const inflightKey = `${key}|t${fetchGen}`;
    const gen = ++enalysisFetchGenerationRef.current;
    let cancelled = false;

    let loadPromise = enalysisTrendParametersInflight.get(inflightKey);
    if (!loadPromise) {
      loadPromise = (async () => {
        if (enalysisFetchGenerationRef.current === gen) {
          setIsLoadingEnalysisTrendValues(false);
        }
        try {
          const compNum = compressor.enalysisCompressorNum;
          const compId = compressor.enalysisCompanyId;
          const [aerialCoolers, cylinders, designConditions, clearances] =
            await Promise.all([
              getEnalysisAerialCoolers(compNum, compId).catch(() => null),
              getEnalysisCylinders(compNum, compId).catch(() => null),
              getEnalysisDesignConditions(compNum, compId).catch(() => null),
              getEnalysisClearances(compNum, compId).catch(() => null),
            ]);
          const enrichmentContext = {
            cylinders,
            designConditions,
            aerialCoolers,
            clearances,
          };
          const parameters = await fetchEnalysisTrendParametersSequential(
            compressor,
            addError,
            {
              onListParams: (listParams) => {
                if (enalysisFetchGenerationRef.current === gen) {
                  setEnalysisTrendParameters(listParams);
                }
              },
              onBeforeTrendValuesFetch: () => {
                if (enalysisFetchGenerationRef.current === gen) {
                  setIsLoadingEnalysisTrendValues(true);
                }
              },
            },
            enrichmentContext
          );
          if (fetchGen === triggerCountRef.current) {
            enalysisTrendParametersCache.set(key, {
              parameters,
              triggerAtFetch: fetchGen,
            });
          }
          return parameters;
        } catch (err) {
          if (shouldReportApiError(err)) {
            addError("Failed to load Enalysis trend list", enalysisErrorDetail(err));
          }
          const empty = [];
          if (fetchGen === triggerCountRef.current) {
            enalysisTrendParametersCache.set(key, {
              parameters: empty,
              triggerAtFetch: fetchGen,
            });
          }
          return empty;
        } finally {
          enalysisTrendParametersInflight.delete(inflightKey);
        }
      })();
      enalysisTrendParametersInflight.set(inflightKey, loadPromise);
    }

    (async () => {
      const parameters = await loadPromise;
      if (cancelled || fetchGen !== triggerCountRef.current) return;
      if (!cancelled) {
        setEnalysisTrendParameters(parameters);
        setIsLoadingEnalysisTrendValues(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enalysisTrendsGate,
    compressor?.id,
    compressor?.enalysisCompressorNum,
    compressor?.enalysisCompanyId,
    triggerCount,
    addError,
  ]);

  // Flatten all parameters into a single array with category info
  const allParameters = useMemo(() => {
    const flatParams = flattenTagDataToParameters(tagData);

    if (enalysisTrendParameters.length === 0) return flatParams;
    return [...flatParams, ...enalysisTrendParameters];
  }, [tagData, enalysisTrendParameters]);

  // Helper: Get unique identifier for a parameter
  const getParameterId = useCallback((param) => {
    const tid = param.apiData?.trendIdentifier;
    if (typeof tid === "string" && tid && ENALYSIS_TREND_ROW_CATEGORIES.has(param.category)) {
      return `enalysis-trend:${tid}`;
    }
    return param.apiData?.assetTagId || `${param.parameter}-${param.category}`;
  }, []);

  // Fetch and cache trend columns using custom hook
  const { trendColumns, trendColumnRawData, isLoadingTrendColumns } =
    useTrendColumns(allParameters, getParameterId, compressor?.id);

  const { trendColumnsWithEnalysis, trendColumnRawDataWithEnalysis } =
    useMemo(() => {
      const cols = new Map(trendColumns);
      const raw = new Map(trendColumnRawData);
      for (const p of allParameters) {
        const m = p.apiData?.enalysisTrendRaw?.Measurements;
        if (p.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE || !m?.length) {
          continue;
        }
        const measurements7d = filterEnalysisMeasurementsByTrendRange(m, "7d");
        const id = getParameterId(p);
        const cv = parseFloat(removeCommasAndSpaces(String(p.currentValue)));
        const numeric = Number.isFinite(cv) ? cv : null;
        cols.set(id, calculateAverageAndZScore(measurements7d, numeric));
        raw.set(id, { Measurements: measurements7d });
      }
      return {
        trendColumnsWithEnalysis: cols,
        trendColumnRawDataWithEnalysis: raw,
      };
    }, [allParameters, trendColumns, trendColumnRawData, getParameterId]);

  // Get selected parameter data from allParameters (not filtered list) by unique IDs
  // This is independent of filters, so it won't change when filters change
  const getSelectedParameterData = useMemo(() => {
    if (selectedParameters.size === 0) return [];
    // Create a map of all parameters by their unique ID for quick lookup
    const paramMap = new Map();
    allParameters.forEach((param) => {
      const id = getParameterId(param);
      paramMap.set(id, param);
    });

    // Return selected parameters from allParameters, not filtered list
    return Array.from(selectedParameters)
      .map((id) => paramMap.get(id))
      .filter(Boolean);
  }, [selectedParameters, allParameters, getParameterId]);

  // Create a map of 7-day data cache for useTrendData hook
  // This is needed because useTrendData expects a Map structure with { rawData } objects
  // This cache is used for sparklines, average, z-score, and as fallback for 7d trend data
  const sevenDayDataCacheForTrend = useMemo(() => {
    const cache = new Map();
    trendColumnRawDataWithEnalysis.forEach((rawData, paramId) => {
      if (rawData) {
        cache.set(paramId, { rawData });
        // Also populate trend cache for 7d period to enable instant switching
        setTrendDataCache((prevCache) => {
          const newCache = new Map(prevCache);
          if (!newCache.has(paramId)) {
            newCache.set(paramId, new Map());
          }
          const paramCache = new Map(newCache.get(paramId));
          // Only set if not already cached (to avoid overwriting)
          if (!paramCache.has("7d")) {
            paramCache.set("7d", rawData);
          }
          newCache.set(paramId, paramCache);
          return newCache;
        });
      }
    });
    return cache;
  }, [trendColumnRawDataWithEnalysis]);

  // Fetch trend data using custom hook
  const { trendData, isLoadingTrend, trendError, fetchTrendData } =
    useTrendData(
      selectedParameters,
      getSelectedParameterData,
      compressor,
      selectedTimeRange,
      customStartDate,
      customEndDate,
      trendDataCache,
      sevenDayDataCacheForTrend,
      getParameterId,
      setTrendDataCache
    );

  // Manual refresh handler
  const handleRefreshTrendData = useCallback(() => {
    fetchTrendData();
  }, [fetchTrendData]);

  // Custom range: chart loads via useTrendData (selected rows only); table stays on 1mo Enalysis data
  const applyCustomDateRange = useCallback(
    (startDate, endDate) => {
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
      fetchTrendData(startDate, endDate);
    },
    [fetchTrendData]
  );

  // Sort handler
  const handleSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn, sortDirection]
  );

  // Format value helper
  const formatValue = useCallback((param) => {
    const baseValue = formatParameterValue(param);
    if (typeof baseValue === "number") {
      return formatNumber(baseValue, 2);
    }
    if (typeof baseValue !== "string") {
      return baseValue;
    }
    const t = baseValue.trim();
    if (!t || t === "—" || t === "-" || t === "No Data") {
      return baseValue;
    }
    const n = parseFloat(removeCommasAndSpaces(t));
    return Number.isFinite(n) ? formatNumber(n, 2) : baseValue;
  }, []);

  // Chart utility functions
  const createChartSeriesMemo = useCallback(
    (data) => createChartSeries(data),
    []
  );

  const formatMultiSeriesTooltipMemo = useCallback(
    (data) => formatMultiSeriesTooltip(data),
    []
  );

  return {
    // Data
    allParameters,
    tagData,
    isLoading,
    isLoadingEnalysisTrendValues,
    error,
    trendColumns: trendColumnsWithEnalysis,
    trendColumnRawData: trendColumnRawDataWithEnalysis,
    isLoadingTrendColumns,
    trendData,
    isLoadingTrend,
    trendError,

    // Filter state
    searchTerm,
    setSearchTerm,
    selectedStatus,
    setSelectedStatus,
    selectedCategory,
    setSelectedCategory,
    selectedParameters,
    setSelectedParameters,

    // Sort state
    sortColumn,
    sortDirection,
    handleSort,

    // Trend state
    selectedTimeRange,
    setSelectedTimeRange,
    previousTimeRange,
    setPreviousTimeRange,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    applyCustomDateRange,
    isZoomed,
    setIsZoomed,

    // Handlers and utilities
    getParameterId,
    formatValue,
    handleRefreshTrendData,
    createChartSeriesMemo,
    formatMultiSeriesTooltipMemo,
  };
}
